/**
 * Normalizza i percorsi documenti salvati per errore:
 * - URI `search-ms:` (Risultati ricerca Windows) → estrae il percorso UNC da `crumb=location:`
 * - Segmento errato `4653_ATTIANI MAURO CDU` inserito tra root NAS e cartella pratica
 */

import { joinUncDocumentsPath } from './joinUncPath';

/** Segmento fantasma ripetuto per errore di selezione cartella (storico). */
const LEGACY_ERRONEOUS_FOLDER = /4653_ATTIANI MAURO CDU/i;

/**
 * Percorso UNC predefinito per dialog Electron se le impostazioni non sono disponibili.
 */
export const DEFAULT_NAS_PICKER_ROOT = '\\\\nas_gori\\dati_nas\\Public\\Volume_1';

function decodeMixedWindowsPathFragment(fragment: string): string {
  let t = fragment.trim();
  if (!t) return '';
  t = t.replace(/%5C/gi, '\\');
  try {
    t = decodeURIComponent(t);
  } catch {
    t = t.replace(/%([0-9A-F]{2})/gi, (_, h) => String.fromCharCode(parseInt(h, 16)));
  }
  return t;
}

/**
 * Estrae un percorso file system da una stringa `search-ms:...` (Risultati ricerca Windows).
 * Deve iniziare con `search-ms:` (usare slice da indexOf se la stringa contiene altro prima).
 */
export function extractUncFromSearchMsUri(raw: string): string | null {
  const s = raw.trim();
  if (!/^search-ms:/i.test(s)) return null;

  const lower = s.toLowerCase();
  const crumbKey = 'crumb=location:';
  const idx = lower.indexOf(crumbKey);
  if (idx !== -1) {
    let fragment = s.slice(idx + crumbKey.length);
    const amp = fragment.indexOf('&');
    if (amp !== -1) fragment = fragment.slice(0, amp);
    const decoded = decodeMixedWindowsPathFragment(fragment);
    if (decoded && (decoded.startsWith('\\\\') || decoded.includes('\\'))) {
      return decoded;
    }
  }

  const m = s.match(/%5C%5C[^&]+/);
  if (m) {
    const decoded = decodeMixedWindowsPathFragment(m[0]);
    if (decoded.startsWith('\\\\')) return decoded;
  }

  return null;
}

/** Estrae UNC da `displayname=Risultati ricerca in \\NAS\...` (senza crumb). */
function extractUncFromSearchMsDisplayname(uriFromSearchMs: string): string | null {
  const lower = uriFromSearchMs.toLowerCase();
  const dIdx = lower.indexOf('displayname=');
  if (dIdx === -1) return null;
  let val = uriFromSearchMs.slice(dIdx + 'displayname='.length);
  const amp = val.indexOf('&');
  if (amp !== -1) val = val.slice(0, amp);
  try {
    val = decodeURIComponent(val.replace(/\+/g, ' '));
  } catch {
    /* testo non encoded */
  }
  // I nomi cartella Windows possono contenere spazi (es. "4744_ARCH. HOSAM HORANY-..."): non escludere \s.
  const rin = val.match(/Risultati\s+ricerca\s+in\s+(\\\\[^\n&]+)/i);
  if (rin) return rin[1].replace(/\\+$/, '');
  const unc = val.match(/(\\\\[A-Za-z0-9_.-]+(?:\\[^\\&]+)+)/);
  if (unc) return unc[1].replace(/\\+$/, '');
  return null;
}

/** Ultimo tentativo: primo UNC plausibile dopo search-ms: */
function extractUncGreedyAfterSearchMs(uriFromSearchMs: string): string | null {
  const m = uriFromSearchMs.match(/\\\\(?:[^\\/]+\\)+[^\\/&]+/);
  return m ? m[0].replace(/\\+$/, '') : null;
}

/**
 * Se crumb dà solo fino a Volume_1 ma prima di search-ms c'è `\4744_...\`, unisce il segmento pratica.
 */
function mergeVolumeWithPracticeSegment(beforeSearchMs: string, extractedUnc: string): string {
  const ext = extractedUnc.trim();
  const bef = beforeSearchMs.trim();
  if (!ext || !/^\\\\/i.test(ext)) return ext;
  if (!/Volume_1(?:\\|$)/i.test(ext)) return ext;
  const parts = bef.split(/[/\\]/).filter(Boolean);
  const lastSeg = parts[parts.length - 1];
  if (!lastSeg || !/^\d{4}_/i.test(lastSeg)) return ext;
  const extNorm = ext.replace(/[/\\]+$/, '');
  if (extNorm.toLowerCase().includes(lastSeg.toLowerCase())) return ext;
  return joinUncDocumentsPath(extNorm, lastSeg);
}

function extractPathFromEmbeddedSearchMs(full: string): string | null {
  const lower = full.toLowerCase();
  const idx = lower.indexOf('search-ms:');
  if (idx === -1) return null;

  const before = full.slice(0, idx).trim();
  const uriPart = full.slice(idx);

  let extracted =
    extractUncFromSearchMsUri(uriPart) ||
    extractUncFromSearchMsDisplayname(uriPart) ||
    extractUncGreedyAfterSearchMs(uriPart);

  if (extracted) {
    extracted = mergeVolumeWithPracticeSegment(before, extracted);
    return extracted;
  }

  if (before && /^\\\\/.test(before)) {
    return before.replace(/[/\\]+$/, '');
  }

  return null;
}

/**
 * Rimuove il segmento cartella errato storico (qualsiasi occorrenza come cartella).
 */
export function stripLegacyErroneousSegment(path: string): string {
  if (!path || !LEGACY_ERRONEOUS_FOLDER.test(path)) return path;
  let out = path.replace(/(^|[\\/])4653_ATTIANI MAURO CDU(?=[\\/]|$)/gi, '$1');
  out = out.replace(/(^|[\\/])4653_ATTIANI MAURO CDU(?=[\\/]|$)/gi, '$1');
  out = out.replace(/\\{3,}/g, '\\');
  return out;
}

/**
 * Normalizza il percorso documenti per uso in UI, filesystem e protocollo `studiogorfile:`.
 */
export function normalizeDocumentPath(raw: string | null | undefined): string {
  if (raw == null || raw === '') return '';
  let s = raw.trim();
  if (!s) return '';

  if (/^search-ms:/i.test(s)) {
    const extracted = extractUncFromSearchMsUri(s);
    if (extracted) s = extracted;
  } else if (s.toLowerCase().includes('search-ms:')) {
    const extracted = extractPathFromEmbeddedSearchMs(s);
    if (extracted) s = extracted;
  }

  s = stripLegacyErroneousSegment(s);
  return s.trim();
}

/**
 * Percorso restituito dal dialog cartella nativo (Electron): attendibile, non applicare
 * euristiche `search-ms` salvo se la stringa contiene davvero quell'URI.
 */
export function normalizePickerResultPath(raw: string | null | undefined): string {
  if (raw == null || raw === '') return '';
  const s = raw.trim();
  if (!s) return '';
  const lower = s.toLowerCase();
  if (lower.startsWith('search-ms:') || lower.includes('search-ms:')) {
    return normalizeDocumentPath(s);
  }
  return stripLegacyErroneousSegment(s).trim();
}

/** Percorso assoluto Windows: UNC (\\...) o unità (C:\...). */
export function isAbsoluteWindowsDocumentPath(p: string): boolean {
  const s = (p || '').trim();
  if (!s) return false;
  if (/^\\\\/.test(s)) return true;
  if (/^[A-Za-z]:[\\/]/.test(s)) return true;
  return false;
}

/**
 * Se `documentPath` è solo il nome cartella o un path relativo (es. `/4748_...` da errori di salvataggio),
 * lo concatena con la root NAS dalle impostazioni. Necessario per Electron che non usa gli handle IndexedDB di Chrome.
 */
export function resolveDocumentPathWithNasRoot(
  documentPath: string | null | undefined,
  nasRoot: string
): string {
  const normalized = normalizeDocumentPath(documentPath ?? '');
  if (!normalized) return '';
  if (isAbsoluteWindowsDocumentPath(normalized)) return normalized;
  const name = normalized.replace(/^[/\\]+/, '');
  if (!name) return '';
  return joinUncDocumentsPath(nasRoot, name);
}
